import { format } from 'date-fns'
import { observer } from 'mobx-react-lite'
import type { AnyQuestionEditor } from '@/features/legends/questions/question-registry'
import {
  QuestionPreview,
  QuestionPreviewButton,
  QuestionPreviewDocuments,
  QuestionPreviewInput,
  QuestionPreviewPills,
} from '@/features/legends/questions/components/QuestionPreview'
import { numberToLocaleString } from '@/utils/number-util'

/**
 * The most decimals the precision field accepts. The preview clamps to it,
 * since the field commits every keystroke on the way to a valid value.
 */
const maxPreviewPlaces = 10

const yesNo = [
  { key: 'yes', label: 'Yes' },
  { key: 'no', label: 'No' },
]

export interface QuestionPreviewContainerProps {
  vm: AnyQuestionEditor
}

/**
 * An asked question as the client sees it. Meta elements have no preview.
 */
export const QuestionPreviewContainer = observer(
  function QuestionPreviewContainer({ vm }: QuestionPreviewContainerProps) {
    switch (vm.type) {
      case 'Bool':
      case 'Breaker':
      case 'DateTime':
      case 'Documents':
      case 'Numeric':
      case 'Select':
      case 'Text':
        return (
          <QuestionPreview
            question={vm.question}
            description={vm.description}
            answer={<AnswerPreviewContainer vm={vm} />}
          />
        )
      case 'Path':
      case 'Variable':
      case 'Effect':
      case 'InventoryItem':
      case 'PersonalOrganizerPrefill':
      case 'PersonalOrganizerCollectedValue':
        return null
    }
  },
)

interface AnswerPreviewContainerProps {
  vm: Extract<
    AnyQuestionEditor,
    {
      type:
        | 'Bool'
        | 'Breaker'
        | 'DateTime'
        | 'Documents'
        | 'Numeric'
        | 'Select'
        | 'Text'
    }
  >
}

const AnswerPreviewContainer = observer(function AnswerPreviewContainer({
  vm,
}: AnswerPreviewContainerProps) {
  switch (vm.type) {
    case 'Bool':
      return <QuestionPreviewPills items={yesNo} />
    case 'Select':
      return (
        <QuestionPreviewPills
          items={vm.options
            .filter((o) => o.id)
            .map((o) => ({ key: o.cid, label: o.text || String(o.id) }))}
          isMulti={vm.multi}
          emptyText="No options yet"
        />
      )
    case 'Text':
      return <QuestionPreviewInput text="Lorem ipsum dolor sit amet" />
    case 'Numeric':
      return (
        <QuestionPreviewInput
          text={numberToLocaleString(
            123,
            Math.min(Math.max(vm.places ?? 0, 0), maxPreviewPlaces),
          )}
          variant="numeric"
        />
      )
    case 'DateTime':
      return (
        <QuestionPreviewInput
          text={format(
            new Date(),
            vm.collectTime ? 'MM/dd/yyyy, h:mm a' : 'MM/dd/yyyy',
          )}
          variant="date"
        />
      )
    case 'Breaker':
      return <QuestionPreviewButton label="Got it" />
    case 'Documents':
      return (
        <QuestionPreviewDocuments
          items={vm.options.map((o, index) => ({
            key: o.id ?? String(index),
            label: o.text || 'Untitled document',
          }))}
        />
      )
  }
})
